/**
 * Rahmen mit Kopfzeile und Navigation.
 * Responsiv: Auf schmalen Bildschirmen wird die Navigation zu einer
 * horizontalen Leiste; Landmarks und Sprunglink sind fuer Screenreader gesetzt.
 */
import { Fragment, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Avatar,
  Body1,
  Caption1,
  Divider,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Button,
  Subtitle2,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  DataBarVerticalRegular,
  GlobeRegular,
  PeopleRegular,
  ArrowSyncRegular,
  SettingsRegular,
  ClipboardTaskListLtrRegular,
  SignOutRegular,
  LocalLanguageRegular,
  MoneyRegular,
  PersonProhibitedRegular,
  DatabaseRegular,
  ReceiptMoneyRegular,
  PlugConnectedRegular,
  BoxRegular,
  LaptopRegular,
  OpenRegular,
  AppsListDetailRegular,
  TagRegular,
} from '@fluentui/react-icons';
import { AuditLogo, AuditWordmark } from './AuditLogo.js';
import { NotificationBell } from './NotificationBell.js';
import { useSession } from '../lib/session.js';
import { useI18n, LOCALES, type LocaleCode } from '../i18n/index.js';
import { PERMISSIONS, type Permission } from '../api/types.js';
import type { TranslationKeys } from '../i18n/de.js';

const useStyles = makeStyles({
  shell: {
    // 100vh zaehlt auf iOS die ein- und ausfahrenden Safari-Leisten mit; die
    // Seite ist dort hoeher als das Sichtfeld. dvh misst das Sichtbare, vh
    // bleibt als Rueckfall fuer aeltere Browser.
    minHeight: '100vh',
    '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    flexWrap: 'wrap',
  },
  brand: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, minWidth: '180px' },
  brandText: { display: 'flex', flexDirection: 'column' },
  brandTitle: {
    // Die Wortmarke traegt "IT" fett; der Buchstabenabstand nimmt die
    // Anmutung des Logos auf, ohne eine eigene Schrift einzubinden.
    letterSpacing: '0.01em',
  },
  brandSubtitle: { color: tokens.colorNeutralForeground3 },
  headerActions: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  userInfo: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', '@media (max-width: 640px)': { display: 'none' } },
  userRoles: { color: tokens.colorNeutralForeground3 },
  body: { display: 'flex', flex: '1 1 auto', alignItems: 'stretch', '@media (max-width: 900px)': { flexDirection: 'column' } },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalS),
    minWidth: '224px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
    borderRightColor: tokens.colorNeutralStroke2,
    '@media (max-width: 900px)': {
      flexDirection: 'row',
      overflowX: 'auto',
      minWidth: 'auto',
      borderRightStyle: 'none',
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: tokens.colorNeutralStroke2,
    },
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    color: tokens.colorNeutralForeground1,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  navLabel: { flexGrow: 1 },
  externalHint: { color: tokens.colorNeutralForeground3, flexShrink: 0 },
  navLinkActive: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    fontWeight: tokens.fontWeightSemibold,
    // Gelbe Kante als Marken-Akzent. Griffel erlaubt keine Kurzschreibweise,
    // daher die Langform je Eigenschaft.
    borderInlineStartWidth: '3px',
    borderInlineStartStyle: 'solid',
    borderInlineStartColor: tokens.colorBrandStroke1,
  },
  navLinkInactive: {
    // Gleiche Breite wie der aktive Zustand, damit die Beschriftungen beim
    // Wechsel nicht springen.
    borderInlineStartWidth: '3px',
    borderInlineStartStyle: 'solid',
    borderInlineStartColor: 'transparent',
  },
  // minWidth 0 ist der entscheidende Wert: Ein Flex-Element ist sonst
  // mindestens so breit wie sein breitester Inhalt. Die 96 rem breite
  // Preistabelle drueckte damit die ganze Seite auf und der Browser scrollte
  // die Seite statt der Tabelle.
  main: {
    flex: '1 1 auto',
    minWidth: 0,
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXL),
    maxWidth: '100%',
  },
  // Unterpunkte einer Gruppe: eingerueckt und etwas kleiner, damit die
  // Hierarchie ohne Trennlinien erkennbar bleibt.
  navChild: {
    paddingInlineStart: tokens.spacingHorizontalXXL,
    fontSize: tokens.fontSizeBase200,
    '@media (max-width: 900px)': { paddingInlineStart: tokens.spacingHorizontalM },
  },
});

interface NavItem {
  to: string;
  labelKey: TranslationKeys;
  icon: JSX.Element;
  permission: Permission;
  /** Unterpunkte; die Gruppe erscheint, sobald mindestens einer sichtbar ist. */
  children?: NavItem[];
  /** true = nur bei exakt diesem Pfad aktiv (Gruppenkopf, dessen Pfad weiterleitet). */
  end?: boolean;
  /**
   * true = oeffnet in einem neuen Tab und wird als solches gekennzeichnet.
   *
   * Fuer Ziele, die eine eigene Oberflaeche haben: Der IT-Katalog laeuft ohne
   * diese Navigation, im selben Tab geoeffnet gaebe es keinen Weg zurueck in
   * die Verwaltung. Ein neuer Tab laesst die Arbeitsumgebung stehen.
   */
  external?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', labelKey: 'nav.dashboard', icon: <DataBarVerticalRegular />, permission: PERMISSIONS.DashboardRead },
  { to: '/laender', labelKey: 'nav.countries', icon: <GlobeRegular />, permission: PERMISSIONS.ReportRead },
  { to: '/zuweisungen', labelKey: 'nav.assignments', icon: <PeopleRegular />, permission: PERMISSIONS.ReportRead },
  { to: '/personen', labelKey: 'nav.people', icon: <MoneyRegular />, permission: PERMISSIONS.ReportRead },
  {
    to: '/verwaiste-lizenzen',
    labelKey: 'nav.orphans',
    icon: <PersonProhibitedRegular />,
    permission: PERMISSIONS.ReportRead,
  },
  { to: '/synchronisierung', labelKey: 'nav.sync', icon: <ArrowSyncRegular />, permission: PERMISSIONS.SyncRead },
  {
    to: '/katalog',
    labelKey: 'nav.catalog',
    icon: <BoxRegular />,
    permission: PERMISSIONS.CatalogRead,
    end: true,
    external: true,
    children: [
      {
        to: '/katalog/verwaltung',
        labelKey: 'catalog.admin.title',
        icon: <AppsListDetailRegular />,
        permission: PERMISSIONS.CatalogWrite,
      },
      {
        to: '/katalog/kategorien',
        labelKey: 'catalog.admin.categories',
        icon: <TagRegular />,
        permission: PERMISSIONS.CatalogWrite,
      },
    ],
  },
  {
    // Der Geraetebestand gehoert zur Verwaltungsarbeit und bleibt deshalb hier.
    // Die Selbstauskunft „Meine Geräte“ liegt bewusst im Katalog – dort, wo
    // Mitarbeitende ohnehin hinschauen.
    to: '/geraete',
    labelKey: 'assets.admin.title',
    icon: <LaptopRegular />,
    permission: PERMISSIONS.AssetManage,
  },
  {
    to: '/administration',
    labelKey: 'nav.admin',
    icon: <SettingsRegular />,
    permission: PERMISSIONS.ProviderRead,
    end: true,
    children: [
      { to: '/administration/quellen', labelKey: 'admin.sources', icon: <DatabaseRegular />, permission: PERMISSIONS.ProviderRead },
      { to: '/administration/preise', labelKey: 'admin.prices', icon: <ReceiptMoneyRegular />, permission: PERMISSIONS.ProviderRead },
      {
        to: '/administration/anbindungen',
        labelKey: 'admin.availableConnectors',
        icon: <PlugConnectedRegular />,
        permission: PERMISSIONS.ProviderRead,
      },
      { to: '/administration/audit', labelKey: 'nav.audit', icon: <ClipboardTaskListLtrRegular />, permission: PERMISSIONS.AuditRead },
    ],
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const styles = useStyles();
  const { profile, has, signOut } = useSession();
  const { t, locale, setLocale } = useI18n();
  const location = useLocation();

  // Gruppen zeigen nur die erlaubten Unterpunkte. Der Gruppenkopf bleibt
  // sichtbar, sobald die eigene Berechtigung reicht – beim IT-Katalog ist er
  // selbst die Uebersicht, die allen Lesenden offensteht, waehrend die
  // Pflegeseiten darunter nur Verwaltenden erscheinen. Ein Kopf ohne eigene
  // Berechtigung (Administration: nur eine Weiterleitung) erscheint dagegen
  // erst, wenn wenigstens ein Unterpunkt sichtbar ist.
  const visibleItems = NAV_ITEMS.flatMap((item) => {
    const children = item.children?.filter((child) => has(child.permission));
    if (has(item.permission)) return [{ ...item, children }];
    return children && children.length > 0 ? [{ ...item, children }] : [];
  });
  const roleLabels = (profile?.roles ?? [])
    .map((role) => {
      const key = `role.${role}` as TranslationKeys;
      const label = t(key);
      return label === key ? role : label;
    })
    .join(', ');

  return (
    <div className={styles.shell}>
      <a className="skip-link" href="#hauptinhalt">
        Zum Hauptinhalt springen
      </a>

      <header className={styles.header}>
        <div className={styles.brand}>
          <AuditLogo size={36} title={t('app.title')} />
          <div className={styles.brandText}>
            <Subtitle2 className={styles.brandTitle}>
              <AuditWordmark />
              {' — '}
              {t('app.productSuffix')}
            </Subtitle2>
            <Caption1 className={styles.brandSubtitle}>{t('app.subtitle')}</Caption1>
          </div>
        </div>

        <div className={styles.headerActions}>
          <NotificationBell />
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button appearance="subtle" icon={<LocalLanguageRegular />} aria-label="Sprache wählen">
                {LOCALES[locale].label}
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {(Object.keys(LOCALES) as LocaleCode[]).map((code) => (
                  <MenuItem key={code} onClick={() => setLocale(code)} disabled={code === locale}>
                    {LOCALES[code].label}
                  </MenuItem>
                ))}
              </MenuList>
            </MenuPopover>
          </Menu>

          <div className={styles.userInfo}>
            <Body1>{profile?.displayName ?? profile?.userPrincipalName ?? ''}</Body1>
            <Caption1 className={styles.userRoles}>{roleLabels}</Caption1>
          </div>

          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="transparent"
                aria-label={profile?.displayName ?? 'Benutzerkonto'}
                icon={<Avatar name={profile?.displayName ?? profile?.userPrincipalName ?? '?'} size={32} color="colorful" />}
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<SignOutRegular />} onClick={signOut}>
                  {t('auth.signOut')}
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </header>

      <div className={styles.body}>
        <nav className={styles.nav} aria-label={t('app.title')}>
          {visibleItems.map((item) => (
            <Fragment key={item.to}>
              {item.external ? (
                // rel="noreferrer noopener" gehoert zu jedem target="_blank":
                // Ohne noopener kann die geoeffnete Seite ueber window.opener
                // auf diesen Tab zugreifen.
                <a
                  href={item.to}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={`${styles.navLink} ${styles.navLinkInactive}`}
                >
                  {item.icon}
                  <span className={styles.navLabel}>{t(item.labelKey)}</span>
                  <OpenRegular className={styles.externalHint} aria-label={t('nav.opensInNewTab')} />
                </a>
              ) : (
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `${styles.navLink} ${isActive ? styles.navLinkActive : styles.navLinkInactive}`
                  }
                  aria-current={location.pathname === item.to ? 'page' : undefined}
                >
                  {item.icon}
                  <span>{t(item.labelKey)}</span>
                </NavLink>
              )}
              {item.children?.map((child) => (
                <NavLink
                  key={child.to}
                  to={child.to}
                  className={({ isActive }) =>
                    `${styles.navLink} ${styles.navChild} ${isActive ? styles.navLinkActive : styles.navLinkInactive}`
                  }
                  aria-current={location.pathname === child.to ? 'page' : undefined}
                >
                  {child.icon}
                  <span>{t(child.labelKey)}</span>
                </NavLink>
              ))}
            </Fragment>
          ))}
        </nav>

        <Divider vertical aria-hidden />

        <main id="hauptinhalt" className={styles.main}>
          {children}
        </main>
      </div>
    </div>
  );
}
